import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, FolderPlus, Loader2, Package, Plus, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../shared/Toast';
import { rawMaterialsAPI, suppliersAPI } from '../../services/warehouseAPI';
import { RawMaterial, Supplier, UnitType } from '../../types/warehouse';
import { API_BASE_URL } from '../config/api';
import { formatMoney } from '../../utils/formatMoney';
import { LoadFailed } from '../settings/instantSettings';
import { SheetField } from '../settings/sheet';
import {
  Column, DataTable, DetailPanel, EmptyState, FilterSelect, GHOST_BTN, INPUT_CLS, KeyValue, ListToolbar, PRIMARY_BTN,
  SELECT_CLS, StatusChip, matchesSearch,
} from '../shared/listPage';
import { STOCK_TONES, StockState, stockStateOf } from './warehouseUi';

/**
 * Αποθήκη → «Πρώτες ύλες» (14/10/2026). Δύο επίπεδα όπως πριν: πρώτα οι κατηγορίες (με αξία και ειδοποιήσεις,
 * ως 14/10 κάρτες με χρωματιστό περίγραμμα), μετά οι πρώτες ύλες της κατηγορίας με αναζήτηση και φίλτρο
 * αποθέματος. Η κατηγορία αλλάζει από το φίλτρο «Κατηγορία» (το «Όλες οι κατηγορίες» είναι το παλιό «Προβολή
 * όλων των κατηγοριών»). Φόρμα πρώτης ύλης, προσαρμογή αποθέματος και νέα κατηγορία σε συρτάρια. Ίδιες
 * κλήσεις: rawMaterialsAPI.getAll/create/update/adjustStock, suppliersAPI.getAll(active), GET/POST
 * /api/warehouse/material-categories· ίδια επικύρωση του browser (όνομα, μονάδα, ποσότητα, όνομα κατηγορίας).
 *
 * Το φίλτρο αποθέματος (Όλα / Χαμηλό / Εξαντλημένο) ως 14/10 άλλαζε μόνο το κουμπί: η λίστα φιλτραριζόταν μόνο
 * στην επόμενη αποθήκευση. Τώρα φιλτράρει αμέσως, στη μνήμη, με τους ίδιους κανόνες (χαμηλό = όπως το
 * low_stock του server, απόθεμα ≤ ελάχιστο· εξαντλημένο = απόθεμα 0)· τα αιτήματα έμειναν ίδια.
 */

const UNIT_TYPES: UnitType[] = ['ΤΕΜΑΧΙΑ', 'ΛΙΤΡΑ', 'ΚΙΛΑ', 'ΓΡΑΜΜΑΡΙΑ'];

type FilterStatus = 'all' | 'low' | 'out';
type Drawer = 'material' | 'adjust' | 'category' | null;

interface CategoryRow {
  id: number;
  name: string;
  description?: string;
  material_count?: number;
}

interface CategorySummary {
  category: CategoryRow;
  count: number;
  totalValue: number;
  lowStockCount: number;
  outOfStockCount: number;
  status: 'out-of-stock' | 'low-stock' | 'ok';
}

const emptyMaterial = (): Partial<RawMaterial> => ({
  name: '',
  material_code: '',
  supplier_id: undefined,
  category_id: undefined,
  purchase_unit: 'ΤΕΜΑΧΙΑ',
  purchase_price: 0,
  min_stock_level: 0,
  max_stock_level: undefined,
  notes: ''
});

const emptyAdjustment = () => ({
  adjustment_type: 'ADJUSTMENT' as 'ADJUSTMENT' | 'WASTE' | 'RETURN',
  quantity: 0,
  reason: ''
});

const emptyCategory = () => ({ name: '', description: '', display_order: 0 });

const RawMaterialsManagement: React.FC = () => {
  const { t } = useTranslation(['warehouse', 'common', 'listPage']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const uid = useId();
  const [materials, setMaterials] = useState<RawMaterial[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [drawer, setDrawer] = useState<Drawer>(null);
  const [saving, setSaving] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<RawMaterial | null>(null);
  const [adjustingMaterial, setAdjustingMaterial] = useState<RawMaterial | null>(null);
  const [formData, setFormData] = useState<Partial<RawMaterial>>(emptyMaterial);
  const [adjustmentData, setAdjustmentData] = useState(emptyAdjustment);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);

  useEffect(() => {
    if (store?.id) {
      fetchSuppliers();
      fetchCategories().then(() => {
        fetchMaterials(); // Fetch materials after categories are loaded
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (filterStatus === 'low') filters.low_stock = true;

      const data = await rawMaterialsAPI.getAll(store!.id, filters);

      // Apply out of stock filter locally
      let filtered = data || [];
      if (filterStatus === 'out') {
        filtered = filtered.filter(m => m.current_stock === 0);
      }

      setMaterials(filtered);
      setLoadFailed(false);
    } catch (error) {
      console.error('Failed to fetch materials:', error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const data = await suppliersAPI.getAll(store!.id, true);
      setSuppliers(data || []);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('xentrix_token');
      const response = await fetch(`${API_BASE_URL}/api/warehouse/material-categories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
        return data; // Return the data for chaining
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
    return [];
  };

  const retryLoad = () => {
    fetchCategories().then(() => fetchMaterials());
  };

  const closeDrawer = () => {
    setDrawer(null);
    setEditingMaterial(null);
    setAdjustingMaterial(null);
    setFormData(emptyMaterial());
    setAdjustmentData(emptyAdjustment());
    setCategoryForm(emptyCategory());
  };

  const requestClose = () => {
    if (saving) return;
    closeDrawer();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const dataToSubmit = {
        ...formData,
        initial_stock: editingMaterial ? undefined : formData.current_stock
      };

      if (editingMaterial) {
        await rawMaterialsAPI.update(store!.id, editingMaterial.id, dataToSubmit);
      } else {
        await rawMaterialsAPI.create(store!.id, dataToSubmit);
      }
      fetchMaterials();
      closeDrawer();
    } catch (error) {
      console.error('Failed to save material:', error);
      showToast('error', t('warehouse:failedSaveMaterial'));
    } finally {
      setSaving(false);
    }
  };

  const handleAdjustStock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adjustingMaterial || saving) return;
    setSaving(true);
    try {
      await rawMaterialsAPI.adjustStock(store!.id, adjustingMaterial.id, adjustmentData);
      fetchMaterials();
      closeDrawer();
    } catch (error) {
      console.error('Failed to adjust stock:', error);
      showToast('error', t('warehouse:failedAdjustStock'));
    } finally {
      setSaving(false);
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('xentrix_token');
      const response = await fetch(`${API_BASE_URL}/api/warehouse/material-categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(categoryForm)
      });

      if (response.ok) {
        await fetchCategories();
        closeDrawer();
      } else {
        const error = await response.json();
        showToast('error', error.error || t('warehouse:failedCreateCategory'));
      }
    } catch (error) {
      console.error('Failed to create category:', error);
      showToast('error', t('warehouse:failedCreateCategory'));
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setEditingMaterial(null);
    setFormData(emptyMaterial());
    setDrawer('material');
  };

  const handleEdit = (material: RawMaterial) => {
    setEditingMaterial(material);
    setFormData({
      name: material.name,
      material_code: material.material_code || '',
      supplier_id: material.supplier_id,
      category_id: material.category_id,
      purchase_unit: material.purchase_unit,
      purchase_price: material.purchase_price,
      min_stock_level: material.min_stock_level,
      max_stock_level: material.max_stock_level,
      notes: material.notes || ''
    });
    setDrawer('material');
  };

  const openAdjust = (material: RawMaterial) => {
    setAdjustingMaterial(material);
    setAdjustmentData(emptyAdjustment());
    setDrawer('adjust');
  };

  const openCategory = () => {
    setCategoryForm(emptyCategory());
    setDrawer('category');
  };

  // ── Κείμενα / κατάσταση ──────────────────────────────────────

  const stateLabel = (state: StockState) => t(`warehouse:ui.status.${state}`);

  const stockChip = (material: RawMaterial) => {
    const state = stockStateOf(material.current_stock, material.stock_status);
    return <StatusChip tone={STOCK_TONES[state]}>{stateLabel(state)}</StatusChip>;
  };

  const summaries: CategorySummary[] = categories.map((category: CategoryRow) => {
    // Filter materials by category_id
    const categoryMaterials = materials.filter(m => m.category_id === category.id);
    const totalValue = categoryMaterials.reduce((sum, m) => sum + (m.current_stock * m.purchase_price), 0);
    const lowStockCount = categoryMaterials.filter(m => m.current_stock > 0 && m.current_stock <= m.min_stock_level).length;
    const outOfStockCount = categoryMaterials.filter(m => m.current_stock <= 0).length; // Include negative values as out of stock
    return {
      category,
      count: (category.material_count ?? 0) > 0 ? Number(category.material_count) : categoryMaterials.length,
      totalValue,
      lowStockCount,
      outOfStockCount,
      status: outOfStockCount > 0 ? 'out-of-stock' : lowStockCount > 0 ? 'low-stock' : 'ok',
    };
  });

  const filteredMaterials = materials.filter(material => {
    const matchesText = matchesSearch(searchTerm, material.name, material.material_code);
    const matchesCategory = !selectedCategory || material.category_id === selectedCategory.id;
    const matchesStatus = filterStatus === 'all'
      || (filterStatus === 'low' && material.current_stock <= material.min_stock_level)
      || (filterStatus === 'out' && material.current_stock === 0);
    return matchesText && matchesCategory && matchesStatus;
  });

  const categoryColumns: Column<CategorySummary>[] = [
    {
      key: 'name',
      header: t('warehouse:ui.materials.category'),
      width: 'minmax(200px, 2fr)',
      stack: 'title',
      render: s => (
        <>
          <span className="block font-medium">{s.category.name}</span>
          {s.category.description && <span className="block text-xs text-t3 truncate">{s.category.description}</span>}
        </>
      ),
      stackRender: s => s.category.name,
    },
    {
      key: 'count',
      header: t('warehouse:ui.materials.materialsColumn'),
      width: '110px',
      align: 'right',
      render: s => <span className="tabular-nums">{s.count}</span>,
      stackRender: s => t('warehouse:ui.materials.materialsCount', { count: s.count }),
    },
    {
      key: 'value',
      header: t('warehouse:ui.materials.value'),
      width: '130px',
      align: 'right',
      render: s => <span className="font-medium">{formatMoney(s.totalValue)}</span>,
      stackRender: s => formatMoney(s.totalValue),
    },
    {
      key: 'low',
      header: t('warehouse:ui.materials.lowColumn'),
      width: '100px',
      align: 'right',
      hideBelow: 'medium',
      render: s => <span className={s.lowStockCount > 0 ? 'tabular-nums text-amber-800 dark:text-amber-300 font-medium' : 'tabular-nums text-t4'}>{s.lowStockCount}</span>,
    },
    {
      key: 'out',
      header: t('warehouse:ui.materials.outColumn'),
      width: '110px',
      align: 'right',
      hideBelow: 'medium',
      render: s => <span className={s.outOfStockCount > 0 ? 'tabular-nums text-red-700 dark:text-red-400 font-medium' : 'tabular-nums text-t4'}>{s.outOfStockCount}</span>,
    },
    {
      key: 'status',
      header: t('warehouse:ui.materials.status'),
      width: '150px',
      stack: 'end',
      render: s => (s.status === 'out-of-stock'
        ? <StatusChip tone="danger">{t('warehouse:ui.status.out')}</StatusChip>
        : s.status === 'low-stock'
          ? <StatusChip tone="warn">{t('warehouse:ui.status.low')}</StatusChip>
          : <StatusChip tone="ok">{t('warehouse:ui.status.ok')}</StatusChip>),
    },
  ];

  const materialColumns: Column<RawMaterial>[] = [
    {
      key: 'name',
      header: t('warehouse:ui.materials.material'),
      width: 'minmax(180px, 2fr)',
      stack: 'title',
      render: m => (
        <>
          <span className="block font-medium">{m.name}</span>
          {m.material_code && <span className="block text-xs text-t3">{m.material_code}</span>}
        </>
      ),
      stackRender: m => m.name,
    },
    {
      key: 'supplier',
      header: t('warehouse:ui.materials.supplier'),
      width: 'minmax(130px, 1.2fr)',
      hideBelow: 'medium',
      render: m => (m.supplier_name ? <span className="text-t2">{m.supplier_name}</span> : <span className="text-t4">—</span>),
    },
    {
      // Απόθεμα και μονάδα μαζί («18 ΛΙΤΡΑ», από κάτω το ελάχιστο): μία στήλη λιγότερη, για να χωρά η «Προσαρμογή».
      key: 'stock',
      header: t('warehouse:ui.materials.stock'),
      width: '150px',
      align: 'right',
      render: m => (
        <>
          <span className="inline-flex items-center gap-1.5 font-medium tabular-nums">
            {m.current_stock < m.min_stock_level && (
              <AlertTriangle className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400" aria-label={t('warehouse:ui.materials.belowMin')} />
            )}
            {m.current_stock} <span className="font-normal text-t2">{m.purchase_unit}</span>
          </span>
          <span className="block text-xs text-t3 tabular-nums">{t('warehouse:ui.minShort', { value: m.min_stock_level })}</span>
        </>
      ),
      stackRender: m => `${m.current_stock} ${m.purchase_unit}`,
    },
    {
      key: 'price',
      header: t('warehouse:ui.materials.price'),
      width: '110px',
      align: 'right',
      hideBelow: 'medium',
      render: m => <span className="text-t2">{formatMoney(Number(m.purchase_price || 0))}</span>,
    },
    {
      key: 'value',
      header: t('warehouse:ui.materials.value'),
      width: '120px',
      align: 'right',
      render: m => <span className="font-medium">{formatMoney(Number(m.current_stock || 0) * Number(m.purchase_price || 0))}</span>,
    },
    {
      key: 'status',
      header: t('warehouse:ui.materials.status'),
      width: '150px',
      render: stockChip,
      stackRender: m => stateLabel(stockStateOf(m.current_stock, m.stock_status)),
    },
    {
      key: 'adjust',
      header: <span className="sr-only">{t('warehouse:ui.materials.adjust')}</span>,
      width: '132px',
      align: 'right',
      stack: 'end',
      // Η προσαρμογή αποθέματος μένει ένα πάτημα από τη λίστα (όπως το παλιό κουμπί)· η γραμμή ανοίγει τη φόρμα.
      render: m => (
        <button
          type="button"
          onClick={() => openAdjust(m)}
          aria-label={t('warehouse:ui.materials.adjustLabel', { name: m.name })}
          className="inline-flex items-center gap-1.5 min-h-[36px] px-2.5 rounded-lg text-sm font-medium text-t2 hover:text-t1 hover:bg-surface-3 whitespace-nowrap transition-colors"
        >
          <SlidersHorizontal className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          {t('warehouse:ui.materials.adjust')}
        </button>
      ),
    },
  ];

  // ── Φόρμες ───────────────────────────────────────────────────

  const fid = (name: string) => `${uid}-${name}`;
  const numberCls = `${INPUT_CLS} tabular-nums`;

  const materialForm = (
    <form id={fid('material-form')} onSubmit={handleSubmit} className="space-y-4" data-testid="material-form">
      <div className="xs-grid2">
        <SheetField label={t('warehouse:ui.fields.nameRequired')} htmlFor={fid('name')}>
          <input id={fid('name')} type="text" value={formData.name} required autoComplete="off" className={INPUT_CLS}
            onChange={e => setFormData({ ...formData, name: e.target.value })} />
        </SheetField>
        <SheetField label={t('warehouse:ui.fields.materialCode')} htmlFor={fid('code')}>
          <input id={fid('code')} type="text" value={formData.material_code} autoComplete="off" className={INPUT_CLS}
            onChange={e => setFormData({ ...formData, material_code: e.target.value })} />
        </SheetField>
      </div>
      <div className="xs-grid2">
        <SheetField label={t('warehouse:ui.fields.supplier')} htmlFor={fid('supplier')}>
          <select id={fid('supplier')} className={SELECT_CLS} value={formData.supplier_id || ''}
            onChange={e => setFormData({ ...formData, supplier_id: e.target.value ? parseInt(e.target.value) : undefined })}>
            <option value="">{t('warehouse:ui.fields.noSupplier')}</option>
            {suppliers.map(supplier => (
              <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
            ))}
          </select>
        </SheetField>
        <SheetField label={t('warehouse:ui.fields.category')} htmlFor={fid('category')}>
          <select id={fid('category')} className={SELECT_CLS} value={formData.category_id || ''}
            onChange={e => {
              const categoryId = e.target.value ? parseInt(e.target.value) : undefined;
              setFormData({ ...formData, category_id: categoryId });
            }}>
            <option value="">{t('warehouse:selectCategory')}</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
                {cat.material_count > 0 && ` (${t('warehouse:materialsCount', { count: cat.material_count })})`}
              </option>
            ))}
          </select>
        </SheetField>
      </div>
      <div className={editingMaterial ? 'xs-grid2' : 'grid grid-cols-1 sm:grid-cols-3 gap-4'}>
        <SheetField label={t('warehouse:ui.fields.purchaseUnit')} htmlFor={fid('unit')}>
          <select id={fid('unit')} className={SELECT_CLS} value={formData.purchase_unit} required
            onChange={e => setFormData({ ...formData, purchase_unit: e.target.value as UnitType })}>
            {UNIT_TYPES.map(unit => (
              <option key={unit} value={unit}>{unit}</option>
            ))}
          </select>
        </SheetField>
        <SheetField label={t('warehouse:ui.fields.purchasePrice')} htmlFor={fid('price')}>
          <input id={fid('price')} type="number" step="0.01" value={formData.purchase_price} className={numberCls}
            onChange={e => setFormData({ ...formData, purchase_price: parseFloat(e.target.value) || 0 })} />
        </SheetField>
        {!editingMaterial && (
          <SheetField label={t('warehouse:ui.fields.initialStock')} htmlFor={fid('initial')}>
            <input id={fid('initial')} type="number" step="0.001" value={formData.current_stock || 0} className={numberCls}
              onChange={e => setFormData({ ...formData, current_stock: parseFloat(e.target.value) || 0 })} />
          </SheetField>
        )}
      </div>
      <div className="xs-grid2">
        <SheetField label={t('warehouse:ui.fields.minStock')} htmlFor={fid('min')}>
          <input id={fid('min')} type="number" step="0.001" value={formData.min_stock_level} className={numberCls}
            onChange={e => setFormData({ ...formData, min_stock_level: parseFloat(e.target.value) || 0 })} />
        </SheetField>
        <SheetField label={t('warehouse:ui.fields.maxStock')} htmlFor={fid('max')}>
          <input id={fid('max')} type="number" step="0.001" value={formData.max_stock_level || ''} className={numberCls}
            onChange={e => setFormData({ ...formData, max_stock_level: e.target.value ? parseFloat(e.target.value) : undefined })} />
        </SheetField>
      </div>
      <SheetField label={t('common:notes')} htmlFor={fid('notes')}>
        <textarea id={fid('notes')} rows={3} value={formData.notes} className={`${INPUT_CLS} resize-y`}
          onChange={e => setFormData({ ...formData, notes: e.target.value })} />
      </SheetField>
    </form>
  );

  const adjustForm = adjustingMaterial && (
    <form id={fid('adjust-form')} onSubmit={handleAdjustStock} className="space-y-4" data-testid="adjust-form">
      <KeyValue items={[
        { id: 'material', label: t('warehouse:ui.materials.material'), value: adjustingMaterial.name },
        { id: 'stock', label: t('warehouse:ui.adjust.current'), value: `${adjustingMaterial.current_stock} ${adjustingMaterial.purchase_unit}` },
      ]} />
      <SheetField label={t('warehouse:ui.adjust.type')} htmlFor={fid('adjust-type')}>
        <select id={fid('adjust-type')} className={SELECT_CLS} value={adjustmentData.adjustment_type}
          onChange={e => setAdjustmentData({ ...adjustmentData, adjustment_type: e.target.value as 'ADJUSTMENT' | 'WASTE' | 'RETURN' })}>
          <option value="ADJUSTMENT">{t('warehouse:adjustmentOption')}</option>
          <option value="WASTE">{t('warehouse:wasteOption')}</option>
          <option value="RETURN">{t('warehouse:returnToSupplier')}</option>
        </select>
      </SheetField>
      <SheetField
        label={adjustmentData.adjustment_type === 'RETURN' ? t('warehouse:ui.adjust.quantityAdd') : t('warehouse:ui.adjust.quantityRemove')}
        htmlFor={fid('adjust-qty')}
      >
        <input id={fid('adjust-qty')} type="number" step="0.001" value={adjustmentData.quantity} required className={`${numberCls} sm:max-w-[12rem]`}
          onChange={e => setAdjustmentData({ ...adjustmentData, quantity: parseFloat(e.target.value) || 0 })} />
      </SheetField>
      <SheetField label={t('warehouse:ui.adjust.reason')} htmlFor={fid('adjust-reason')}>
        <textarea id={fid('adjust-reason')} rows={3} value={adjustmentData.reason} placeholder={t('warehouse:reasonPlaceholder')}
          className={`${INPUT_CLS} resize-y`} onChange={e => setAdjustmentData({ ...adjustmentData, reason: e.target.value })} />
      </SheetField>
    </form>
  );

  const categoryFormEl = (
    <form id={fid('category-form')} onSubmit={handleCategorySubmit} className="space-y-4" data-testid="category-form">
      <SheetField label={t('warehouse:ui.category.name')} htmlFor={fid('cat-name')}>
        <input id={fid('cat-name')} type="text" value={categoryForm.name} required placeholder={t('warehouse:categoryNamePlaceholder')}
          autoComplete="off" className={INPUT_CLS} onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })} />
      </SheetField>
      <SheetField label={t('warehouse:ui.category.description')} htmlFor={fid('cat-description')}>
        <input id={fid('cat-description')} type="text" value={categoryForm.description} placeholder={t('warehouse:categoryDescPlaceholder')}
          autoComplete="off" className={INPUT_CLS} onChange={e => setCategoryForm({ ...categoryForm, description: e.target.value })} />
      </SheetField>
      <SheetField label={t('warehouse:ui.category.order')} htmlFor={fid('cat-order')} hint={t('warehouse:displayOrderPlaceholder')}>
        <input id={fid('cat-order')} type="number" value={categoryForm.display_order} className={`${numberCls} sm:max-w-[12rem]`}
          onChange={e => setCategoryForm({ ...categoryForm, display_order: parseInt(e.target.value) || 0 })} />
      </SheetField>
    </form>
  );

  const drawerTitle = drawer === 'material'
    ? (editingMaterial ? t('warehouse:ui.materials.editTitle') : t('warehouse:ui.materials.newTitle'))
    : drawer === 'adjust' ? t('warehouse:ui.adjust.title') : t('warehouse:ui.category.title');

  const drawerFormId = drawer === 'material' ? fid('material-form') : drawer === 'adjust' ? fid('adjust-form') : fid('category-form');

  const submitLabel = drawer === 'material'
    ? (editingMaterial ? t('warehouse:ui.save') : t('warehouse:ui.create'))
    : drawer === 'adjust' ? t('warehouse:ui.adjust.confirm') : t('warehouse:ui.create');

  const categoryFilterOptions = categories.map((c: CategoryRow) => ({ value: String(c.id), label: c.name }));

  // ── Σελίδα ───────────────────────────────────────────────────

  return (
    <>
      <ListToolbar
        search={selectedCategory ? {
          value: searchTerm,
          onChange: setSearchTerm,
          placeholder: t('warehouse:searchMaterials'),
          ariaLabel: t('warehouse:ui.materials.searchLabel'),
        } : undefined}
        filters={(
          <>
            <FilterSelect
              label={t('warehouse:ui.materials.categoryFilter')}
              value={selectedCategory ? String(selectedCategory.id) : ''}
              allLabel={t('warehouse:ui.materials.allCategories')}
              options={categoryFilterOptions}
              onChange={v => setSelectedCategory(v ? categories.find((c: CategoryRow) => String(c.id) === v) || null : null)}
              testId="materials-category-filter"
            />
            {selectedCategory && (
              <FilterSelect
                label={t('warehouse:ui.materials.statusFilter')}
                value={filterStatus === 'all' ? '' : filterStatus}
                allLabel={t('warehouse:ui.materials.statusAll')}
                options={[
                  { value: 'low', label: t('warehouse:ui.status.low') },
                  { value: 'out', label: t('warehouse:ui.status.out') },
                ]}
                onChange={v => setFilterStatus((v || 'all') as FilterStatus)}
                testId="materials-status-filter"
              />
            )}
          </>
        )}
        actions={(
          <>
            {!selectedCategory && (
              <button type="button" className={GHOST_BTN} onClick={openCategory}>
                <FolderPlus className="w-4 h-4" aria-hidden="true" />
                {t('warehouse:ui.materials.addCategory')}
              </button>
            )}
            <button type="button" className={PRIMARY_BTN} onClick={openCreate}>
              <Plus className="w-4 h-4" aria-hidden="true" />
              {t('warehouse:ui.materials.add')}
            </button>
          </>
        )}
        summary={loading || loadFailed ? null : selectedCategory
          ? `${t('warehouse:categoryLabel', { name: selectedCategory.name })} · ${t('warehouse:ui.materials.materialsCount', { count: filteredMaterials.length })}`
          : t('warehouse:ui.materials.categoriesCount', { count: categories.length })}
      />

      {loadFailed ? (
        <LoadFailed message={t('warehouse:ui.materials.loadFailed')} onRetry={retryLoad} />
      ) : !selectedCategory ? (
        <DataTable
          columns={categoryColumns}
          rows={summaries}
          rowKey={s => s.category.id}
          onRowClick={s => setSelectedCategory(s.category)}
          loading={loading}
          stickyHeader
          ariaLabel={t('warehouse:ui.materials.categoriesTable')}
          rowLabel={s => t('warehouse:ui.materials.categoryRowLabel', { name: s.category.name })}
          testId="material-categories-table"
          empty={<EmptyState icon={Package} title={t('warehouse:ui.materials.noCategories')} hint={t('warehouse:ui.materials.noCategoriesHint')} />}
        />
      ) : (
        <DataTable
          columns={materialColumns}
          rows={filteredMaterials}
          rowKey={m => m.id}
          onRowClick={handleEdit}
          selectedKey={drawer === 'material' ? editingMaterial?.id ?? null : drawer === 'adjust' ? adjustingMaterial?.id ?? null : null}
          loading={loading}
          stickyHeader
          ariaLabel={t('warehouse:ui.tabs.rawMaterials')}
          rowLabel={m => t('warehouse:ui.materials.rowLabel', { name: m.name })}
          testId="materials-table"
          empty={searchTerm.trim() || filterStatus !== 'all'
            ? <EmptyState title={t('warehouse:ui.materials.noResults')} hint={t('warehouse:ui.tryAnotherSearch')} />
            : <EmptyState icon={Package} title={t('warehouse:ui.materials.noMaterials')} hint={t('warehouse:ui.materials.noMaterialsHint')} />}
        />
      )}

      <DetailPanel
        open={drawer !== null}
        onClose={requestClose}
        title={drawerTitle}
        subtitle={drawer === 'material' ? editingMaterial?.name : drawer === 'adjust' ? adjustingMaterial?.name : undefined}
        width={drawer === 'material' ? 'lg' : 'md'}
        footer={(
          <>
            <button type="button" className={GHOST_BTN} onClick={requestClose} disabled={saving}>{t('warehouse:cancelShort')}</button>
            <button type="submit" form={drawerFormId} className={PRIMARY_BTN} disabled={saving} aria-busy={saving || undefined}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {submitLabel}
            </button>
          </>
        )}
        testId="material-panel"
      >
        {drawer === 'material' && materialForm}
        {drawer === 'adjust' && adjustForm}
        {drawer === 'category' && categoryFormEl}
      </DetailPanel>
    </>
  );
};

export default RawMaterialsManagement;
